import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Collapse, Typography } from '@mui/material'
import React from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { useUser } from 'src/Hooks/useUser'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { NumberField, Toggle } from 'src/Libs/inputs/react-hook-form'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { UnitTypes } from 'src/Libs/types'
import { calculateFeedInMaxPower } from '../utils/feed-in-max-power'
import { MAXIMUM_PEAK_POWER } from '../const'

export const SystemRegulation: React.FC = () => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control, setValue, getValues } = useInstallationForm()
  const { errors } = useFormState({
    control,
    name: [
      'storageSystem.generalSettings.systemRegulation.peakPower',
      'storageSystem.generalSettings.systemRegulation.hasFeedInLimit',
      'storageSystem.generalSettings.systemRegulation.feedInRatio',
      'storageSystem.generalSettings.systemRegulation.feedInMaxPower',
    ],
  })
  const { userData } = useUser()

  const isUserLevelUser = userData?.level === 'USER'
  const isError = !!(
    errors.storageSystem?.generalSettings?.systemRegulation?.feedInRatio || errors.storageSystem?.generalSettings?.systemRegulation?.feedInMaxPower
  )
  const isFieldDisabled = isUserLevelUser || !canEdit

  const hasFeedInLimit = useWatch({ control, name: 'storageSystem.generalSettings.systemRegulation.hasFeedInLimit' })
  const feedInRatio = useWatch({ control, name: 'storageSystem.generalSettings.systemRegulation.feedInRatio' })

  const handlePeakPowerChange = (newPeakPower: string | number): void => {
    const newPeakPowerNumber: number = Number(newPeakPower) || 0
    const newFeedInMaxPower = roundToTwoDecimal(calculateFeedInMaxPower(newPeakPowerNumber, feedInRatio || 0))

    setValue('storageSystem.generalSettings.systemRegulation.feedInMaxPower', newFeedInMaxPower, { shouldDirty: true, shouldValidate: true })
  }

  const handleFeedInRatioChange = (newFeedInRatio: string | number): void => {
    newFeedInRatio = Number(newFeedInRatio) || 0
    const peakPower = getValues('storageSystem.generalSettings.systemRegulation.peakPower')
    const newFeedInMaxPower = Math.round(calculateFeedInMaxPower(peakPower || 0, newFeedInRatio))
    setValue('storageSystem.generalSettings.systemRegulation.feedInMaxPower', newFeedInMaxPower, { shouldDirty: true, shouldValidate: true })
  }

  const handleFeedInMaxPowerChange = (newFeedInMaxPower: string | number): void => {
    // DecimalField passes the raw input, so normalize comma decimals (e.g. "1,5") before parsing.
    newFeedInMaxPower = String(newFeedInMaxPower).replace(',', '.')
    const normalizedFeedInRatio = Number(feedInRatio) || 0
    const newFeedInMaxPowerNumber = Number.parseFloat(newFeedInMaxPower) || 0
    if (newFeedInMaxPowerNumber === 0 || normalizedFeedInRatio <= 0) {
      setValue('storageSystem.generalSettings.systemRegulation.peakPower', 0, { shouldDirty: true, shouldValidate: true })
      return
    }

    const newPeakPower = roundToTwoDecimal((newFeedInMaxPowerNumber * 100) / normalizedFeedInRatio)
    setValue('storageSystem.generalSettings.systemRegulation.peakPower', newPeakPower, { shouldDirty: true, shouldValidate: true })
  }

  return (
    <>
      <Typography variant="h2" mb={2}>
        {t('installation.storageEditor.systemRegulation')}
      </Typography>
      <SettingsBox
        title={t('installation.storageEditor.peakPower')}
        suffix={
          <NumberField
            name="storageSystem.generalSettings.systemRegulation.peakPower"
            label={t('installation.storageEditor.peakPower')}
            suffix={UnitTypes.wattPeak}
            inputProps={{
              min: 0,
              max: MAXIMUM_PEAK_POWER,
            }}
            propsOnChange={handlePeakPowerChange}
            isDecimal
            disabled={isFieldDisabled}
          />
        }
      />
      <SettingsBox
        title={t('installation.storageEditor.feedInLimit')}
        suffix={<Toggle name="storageSystem.generalSettings.systemRegulation.hasFeedInLimit" disabled={isFieldDisabled} />}
        isSingleLine
      >
        <Collapse in={hasFeedInLimit || isError}>
          <SettingsBox
            title={t('installation.storageEditor.feedValue')}
            suffix={
              <NumberField
                name="storageSystem.generalSettings.systemRegulation.feedInRatio"
                suffix={UnitTypes.percentage}
                inputProps={{
                  min: 0,
                  max: 100,
                }}
                propsOnChange={handleFeedInRatioChange}
                label={t('installation.storageEditor.feedValue')}
                disabled={isFieldDisabled}
              />
            }
            depth={1}
          />

          <SettingsBox
            title={t('installation.storageEditor.reduced')}
            suffix={
              <NumberField
                name="storageSystem.generalSettings.systemRegulation.feedInMaxPower"
                suffix={UnitTypes.watt}
                isDecimal
                inputProps={{
                  min: 0,
                  max: calculateFeedInMaxPower(MAXIMUM_PEAK_POWER, feedInRatio || 0),
                }}
                propsOnChange={handleFeedInMaxPowerChange}
                label={t('installation.storageEditor.reduced')}
                disabled={isFieldDisabled}
              />
            }
            depth={1}
          />
        </Collapse>
      </SettingsBox>
    </>
  )
}

const roundToTwoDecimal = (num: number): number => {
  return Math.round(num * 100) / 100
}
